import React, { useEffect, useRef, useState, ReactNode } from 'react'
import classNames from 'classnames'
import kebabCase from 'lodash.kebabcase'

import { Text } from 'marketing-site/src/library/elements/Text'
import { Icon } from 'marketing-site/src/mvp/library/elements/Icon'
import { IProps } from './index'

const childOf = (
  child: (Node & ParentNode) | null | undefined,
  parent: (Node & ParentNode) | null | undefined,
) => {
  while ((child = child?.parentNode) && child !== parent);
  return !!child
}

export const Dropdown = ({
  placeholder,
  children,
  onSelect,
  isClosedOnSelect = true,
}: IProps): React.ReactElement => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [isActive, setIsActive] = useState<boolean>(false)
  const [selectedOption, setSelectedOption] = useState<number>(0)

  const toggleMenu = () => {
    setIsActive((currentActive) => !currentActive)
  }

  useEffect(() => {
    function onDocumentClick(e: MouseEvent) {
      if (ref.current && !childOf(e.target as Node & ParentNode, ref.current)) setIsActive(false)
    }

    function onDocumentTouchMove(e: Event) {
      if (ref.current && !childOf(e.target as Node & ParentNode, ref.current)) setIsActive(false)
    }

    document.addEventListener('click', onDocumentClick)
    document.addEventListener('touchmove', onDocumentTouchMove)

    return () => {
      document.removeEventListener('click', onDocumentClick)
      document.removeEventListener('touchmove', onDocumentTouchMove)
    }
  }, [])

  const handleSelect = (index: number) => {
    onSelect(index)
    setSelectedOption(index)
    isClosedOnSelect && setIsActive(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, index: number) => {
    switch (e.key) {
      case ' ':
      case 'SpaceBar':
      case 'Enter':
        e.preventDefault()
        handleSelect(index)
        break
      default:
        break
    }
  }

  const menuClasses = classNames('dropdown__menu', {
    'is-expanded': isActive,
  })

  return (
    <div className="dropdown" ref={ref} data-testid="dropdown">
      <button
        type="button"
        onClick={toggleMenu}
        aria-haspopup="listbox"
        aria-expanded={isActive}
        className="dropdown__select"
      >
        <Text size="button-bold">{placeholder}</Text>
        <Icon icon="caret-down-bold" />
      </button>

      {!!children && (
        <ul
          className={menuClasses}
          role="listbox"
          aria-hidden={!isActive}
          aria-activedescendant={`option-${kebabCase(children[selectedOption].props.children)}`}
          tabIndex={-1}
        >
          {React.Children.map(
            children,
            (option: ReactNode, index) =>
              option &&
              React.isValidElement(option) && (
                <li
                  className="dropdown__option"
                  id={`option-${kebabCase(option.props.children)}`}
                  role="option"
                  aria-selected={selectedOption === index}
                  tabIndex={isActive ? 0 : -1}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  onClick={() => handleSelect(index)}
                >
                  {React.cloneElement(option)}
                </li>
              ),
          )}
          <span className="dropdown__menu-arrow" />
        </ul>
      )}

      <style jsx>{`
        .dropdown {
          display: inline-block;
          position: relative;
          z-index: 1;

          &__select {
            display: flex;
            gap: 0.5rem;
            justify-content: space-between;
            align-items: center;
            min-width: 9.75rem;
            width: 100%;
            height: 2.5rem;
            padding: 0 1.5rem;
            border-radius: 2rem;
            background-color: var(--paper);
            box-shadow: inset 0 0 0 2px var(--mvp-slate);
            position: relative;
            cursor: pointer;

            :global(span) {
              white-space: nowrap;
            }

            :global(.icon) {
              transition: transform 0.24s;
            }

            &[aria-expanded='true'] {
              :global(.icon) {
                transform: rotateX(180deg);
              }
            }
          }

          &__menu {
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translate3d(-50%, -0.75rem, 0);
            width: auto;
            max-width: 100vw;
            padding: 1.25rem;
            margin-top: 0.75rem;
            border-radius: 0.5rem;
            background-color: var(--paper);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.04);
            opacity: 0;
            visibility: 0;
            transition: transform 0s 0.32s, opacity 0.32s, visibility 0s 0.32s;
            // This will allow the depth of ::before to change through translateZ().
            transform-style: preserve-3d;

            &.is-expanded {
              transform: translate3d(-50%, 0, 0);
              opacity: 1;
              visibility: visible;
              transition: transform 0.32s, opacity 0.32s, visibility 0s;

              &::before {
                box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.04);
                transition: box-shadow 0.16s 0.32s;
              }
            }
          }

          &__menu-arrow {
            width: 1.5rem;
            height: 0.75rem;
            margin-left: -0.75rem;
            position: absolute;
            left: 50%;
            bottom: 100%;
            overflow: hidden;

            &::after {
              content: '';
              width: 0.75rem;
              height: 0.75rem;
              position: absolute;
              left: 50%;
              transform: translate(-50%, 50%) rotate(45deg);
              background-color: var(--paper);
              box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.04);
            }
          }

          &__option {
            white-space: nowrap;
            cursor: pointer;

            & + & {
              margin-top: 1rem;
            }
          }
        }
      `}</style>
    </div>
  )
}
